import { useQueryClient } from '@tanstack/react-query';
import { Outlet, useMatches, useParams, useRouter, useRouterState } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { Dialog, Modal, ModalOverlay } from 'react-aria-components';
import { meQueryOptions } from '../api/onboarding-queries';
import { useWorkspaces } from '../api/queries';
import { navSectionsFrom, sectionPath, settingsSectionsFrom } from '../app/nav';
import { useSession } from '../app/session-context';
import { reopen } from '../onboarding/dismissal';
import { CommandPalette, usePaletteShortcut, type PaletteAction } from './CommandPalette';
import { Rail } from './Rail';
import { Sidebar } from './Sidebar';
import { TabBar } from './TabBar';
import { TopBar } from './TopBar';

export const COLLAPSE_KEY = 'reportpad.sidebar';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === 'rail';
  } catch {
    return false;
  }
}

function storeCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? 'rail' : 'full');
  } catch {
    // Storage unavailable (private mode): the choice lasts only for this page.
  }
}

/** The /w/$workspaceId layout: navigation, top bar, command palette and the routed screen. */
export function WorkspaceLayout(): ReactElement {
  const { workspaceId } = useParams({ strict: false }) as { workspaceId: string };
  const router = useRouter();
  const queryClient = useQueryClient();
  const { snapshot } = useSession();
  const workspaces = useWorkspaces();
  const routes = Object.values(router.routesById);
  const sections = navSectionsFrom(routes);
  const settings = settingsSectionsFrom(routes);
  const matches = useMatches();
  const activeSection = [...matches].reverse().find((m) => m.staticData?.nav)?.routeId ?? null;
  const crumbs = matches.flatMap((m) => m.staticData?.crumb ?? []);
  const forceRail = matches.some((m) => m.staticData?.rail);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [stored, setStored] = useState(readCollapsed);
  const openPalette = useCallback(() => setPaletteOpen(true), []);
  usePaletteShortcut(openPalette);
  const collapsed = forceRail || stored;
  // One overlay at every width: the rail's Expand on a forced-rail route (the editor)
  // and the top bar's menu button below md both open it. It never writes the stored
  // sidebar preference, which is for the inline sidebar and rail only.
  const [overlayOpen, setOverlayOpen] = useState(false);
  const href = useRouterState({ select: (s) => s.location.href });
  useEffect(() => {
    setOverlayOpen(false);
  }, [href]);

  const setCollapsed = (value: boolean): void => {
    storeCollapsed(value);
    setStored(value);
  };

  const goToReports = useCallback(
    (id: string, create?: 'csv' | 'new'): void => {
      void router.navigate({ to: '/w/$workspaceId/reports', params: { workspaceId: id }, search: { create } });
    },
    [router]
  );

  const others = workspaces.data ?? [];
  const actions = useMemo<PaletteAction[]>(() => {
    const list: PaletteAction[] = [
      { id: 'new-report', label: 'New report', run: () => goToReports(workspaceId, 'new') },
      { id: 'upload-csv', label: 'Upload CSV', run: () => goToReports(workspaceId, 'csv') },
      {
        id: 'getting-started',
        label: 'Getting started',
        run: () => {
          // fetchQuery returns the cached principal, or loads it on a cold palette run.
          void queryClient
            .fetchQuery(meQueryOptions())
            .then(
              (principal) => reopen(principal.id, workspaceId),
              (error) => console.warn('getting started: me failed', error)
            )
            .finally(() => goToReports(workspaceId));
        }
      }
    ];
    if (snapshot.mode === 'accounts') {
      for (const w of others) {
        if (w.id === workspaceId) continue;
        list.push({ id: `switch-${w.id}`, label: `Switch to ${w.name}`, run: () => goToReports(w.id) });
      }
    }
    return list;
  }, [snapshot.mode, others, workspaceId, goToReports, queryClient]);

  return (
    <div className="flex min-h-screen bg-desk">
      <a href="#main" className="sr-only focus:not-sr-only">
        Skip to content
      </a>
      <div className="hidden md:flex">
        {collapsed ? (
          <Rail
            workspaceId={workspaceId}
            sections={sections}
            activeSection={activeSection}
            onExpand={() => (forceRail ? setOverlayOpen(true) : setCollapsed(false))}
          />
        ) : (
          <Sidebar workspaceId={workspaceId} sections={sections} activeSection={activeSection} onCollapse={() => setCollapsed(true)} />
        )}
      </div>
      <ModalOverlay
        isOpen={overlayOpen}
        onOpenChange={setOverlayOpen}
        isDismissable
        className="fixed inset-0 z-drawer m-0 p-0 border-0 bg-scrim flex justify-start"
      >
        <Modal className="w-sidebar h-full m-0 p-0 border-0 shadow-dialog">
          <Dialog aria-label="Navigation" className="m-0 p-0 border-0 outline-none h-full">
            <Sidebar
              workspaceId={workspaceId}
              sections={sections}
              activeSection={activeSection}
              onCollapse={() => setOverlayOpen(false)}
            />
          </Dialog>
        </Modal>
      </ModalOverlay>
      <div className="flex-1 min-w-0 flex flex-col">
        <TopBar
          crumbs={crumbs}
          onSearch={openPalette}
          onMenu={() => setOverlayOpen(true)}
          homeHref={sections[0] ? sectionPath(sections[0], workspaceId) : undefined}
        />
        {/* Below md the tab bar covers the bottom 56px (plus the home indicator); keep content clear of it. */}
        <main id="main" tabIndex={-1} className="flex-1 min-w-0 flex flex-col max-md:pb-tabbar">
          <Outlet key={workspaceId} />
        </main>
      </div>
      <TabBar workspaceId={workspaceId} sections={sections} activeSection={activeSection} />
      <CommandPalette
        workspaceId={workspaceId}
        sections={sections}
        settings={settings}
        isOpen={paletteOpen}
        onOpenChange={setPaletteOpen}
        actions={actions}
      />
    </div>
  );
}
